import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { backendResponse } from '@core/models/http.model'
import { ToasterService } from '@core/services/toaster.service'

@Injectable()
export class HttpService {
  protected readonly _http = inject(HttpClient)
  protected readonly _toasterService = inject(ToasterService)
  protected _baseUrl!: string

  /**
   * @param url Optional: _baseUrl by default
   * @returns The list of element
   */
  // ! Do not modify this function without testing all calls (including errors).
  GETlist(url: string = this._baseUrl): Promise<any> {
    return new Promise((resolve) => {
      this._http.get<any>(url).subscribe({
        next: (response) => {
          if (Array.isArray(response)) {
            const resultSorted = response.sort((A, B) => A.id - B.id)
            return resolve(resultSorted)
          }
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })
    })
  }

  /**
   * @param id ID of object
   * @param url Optional: _baseUrl/id by default
   * @returns The element with the id provided
   */
  // ! Do not modify this function without testing all calls (including errors).
  GETobject(id: any, url: string = `${this._baseUrl}/${id}`): Promise<any> {
    return new Promise((resolve) => {
      this._http.get<any[]>(url).subscribe({
        next: async (response) => {
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })
    })
  }

  // ! Do not modify this function without testing all calls (including errors).
  GET(url: string = `${this._baseUrl}`): Promise<any> {
    return new Promise((resolve) => {
      this._http.get<any[]>(url).subscribe({
        next: async (response) => {
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })
    })
  }

  /**
   * @param url The URL to GET
   * @param headers Additional headers to include in the request
   * @returns The response data
   */
  // ! Do not modify this function without testing all calls (including errors).
  GETwithHeaders(url: string, headers: { [key: string]: string }): Promise<any> {
    return new Promise((resolve) => {
      this._http.get<any[]>(url, { headers }).subscribe({
        next: async (response) => {
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })
    })
  }

  /**
   * @param object Object to add
   * @param url Optional: _baseUrl by default
   * @returns Message define in backend and id of element if add successful
   */
  // ! Do not modify this function without testing all calls (including errors).
  POST(object: any, url: string = this._baseUrl, options?: { signal: AbortSignal }): Promise<any> {
    return new Promise((resolve) => {
      const subscription = this._http.post<backendResponse>(url, object).subscribe({
        next: async (response) => {
          if (response.message) {
            this._toasterService.openSnackBar(response.message)
          }
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })

      if (options?.signal) {
        if (options.signal.aborted) {
          subscription.unsubscribe()
          resolve(null)
        } else {
          options.signal.addEventListener(
            'abort',
            () => {
              subscription.unsubscribe()
              resolve(null)
            },
            { once: true },
          )
        }
      }
    })
  }

  /**
   * @param id ID of object to delete
   * @param url Optional: _baseUrl/id by default
   * @returns Message define in backend and id of element if delete successful
   */
  // ! Do not modify this function without testing all calls (including errors).
  DELETE(id: string | number, url: string = `${this._baseUrl}/${id}`): Promise<backendResponse> {
    return new Promise((resolve) => {
      this._http.delete<backendResponse>(url).subscribe({
        next: async (response) => {
          //@TODO: add a message to /cm/delete/{task_id}
          if (response && response.message) {
            this._toasterService.openSnackBar(response.message)
          }
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve({ message: '' })
        },
      })
    })
  }

  // ! Do not modify this function without testing all calls (including errors).
  DELETEwithHeaders(
    url: string = `${this._baseUrl}`,
    headers: { [key: string]: string },
  ): Promise<backendResponse> {
    return new Promise((resolve) => {
      this._http.delete<backendResponse>(url, { headers }).subscribe({
        next: async (response) => {
          if (response && response.message) {
            this._toasterService.openSnackBar(response.message)
          }
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve({ message: '' })
        },
      })
    })
  }
  /**
   * @param url The URL to POST
   * @param object Object to add
   * @param headers Additional headers to include in the request
   * @returns Message define in backend and id of element if add successful

  */
  // ! Do not modify this function without testing all calls (including errors).
  POSTwithHeaders(url: string, object: any, headers: { [key: string]: string }): Promise<any> {
    return new Promise((resolve) => {
      this._http.post<backendResponse>(url, object, { headers }).subscribe({
        next: async (response) => {
          if (response.message) {
            this._toasterService.openSnackBar(response.message)
          }
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })
    })
  }

  /**
   * @param id Id of object to edit
   * @param object Edited object
   * @param url Optional: _baseUrl/id by default
   * @returns Message define in backend and id of element if edit successful
   */
  // ! Do not modify this function without testing all calls (including errors).
  PUT(id: number, body: any, url: string = `${this._baseUrl}/${id}`): Promise<backendResponse> {
    return new Promise((resolve) => {
      this._http.put<backendResponse>(url, body).subscribe({
        next: async (response) => {
          this._toasterService.openSnackBar(response.message)
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve({ message: '' })
        },
      })
    })
  }

  // ! Do not modify this function without testing all calls (including errors).
  POSTtoGetBlob(body: any, url: string, options?: any): Promise<any> {
    if (!options) {
      options = {
        responseType: 'blob',
      }
    }

    return new Promise((resolve) => {
      this._http.post<backendResponse>(url, body, options).subscribe({
        next: async (response) => {
          return resolve(response)
        },
        error: () => {
          // All http request error are managed by HttpInterceptor
          return resolve(null)
        },
      })
    })
  }
}
